import { useMemo, useCallback, useContext } from "react";
import { IMainTableColumn } from "../../../../../components/MainTable/MainTableInterfaces";
import { useTranslation } from "react-i18next";
import { IEventReport } from "../DetailsAPIInterfaces";
import { Alert, Typography } from "@mui/material";
import { useStyles } from "./useDetailsColumns.styles";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { HMICachedDataContext } from "../../../../../contexts/HMICachedDataContext/HMICachedDataContext";
import {
    IFailureCode,
    IFailureType,
} from "../../../../../contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IArea, IFunctionalLocation } from "../../FailureDetails/FailureDetailsInterfaces";
import { HMIContext } from "../../../../../contexts/HMIContext/HMIContext";
import { FAILUREDETAILSSTEPS, MachineCenterLogTriggersEnum } from "../../../../../constants/constants";
import { IUser } from "../../../../../contexts/UserContext/UserContextInterfaces";
import {
    getCurrentFailureDetailsStep,
    setCurrentArea,
    setCurrentFailureCode,
    setCurrentFailureType,
    setCurrentFunctionalLocation,
    setCurrentNotes,
    setEventReportEditData,
    setEventReportEditing,
} from "utilities/storageUtilities";
import moment from "moment-timezone";
import useMachineCenterLogs from "hooks/useMachineCenterLogs";
import CloudSyncIcon from "@mui/icons-material/CloudSync";

interface IDetailsColumns extends Omit<IMainTableColumn, "render"> {
    render: (
        row: IEventReport,
    ) =>
        | string
        | JSX.Element
        | number
        | {}
        | undefined
        | null
        | Date
        | IFailureType
        | IFailureCode
        | IArea
        | IFunctionalLocation
        | IUser;
}

export const useDetailsColumns = (): IDetailsColumns[] => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { dispatch, store } = useContext(HMICachedDataContext);
    const { triggerMachineCenterLogs } = useMachineCenterLogs();
    const {
        handleSetFailureDetailStep,
        handleFailureTypeChange,
        handleFailureCode,
        handleArea,
        handleFunctionalLocation,
        handleNotes,
        notes,
        failureCode,
        failureType,
        area,
        functionalLocation,
        operator,
    } = useContext(HMIContext);
    const currentFailureDetailsStepFromLocalStorage = getCurrentFailureDetailsStep();

    const userTimezone = useMemo(() => moment.tz.guess(), []);

    const handleEdit = useCallback(
        (row: IEventReport["fullEventReportObject"]) => {
            return (event: React.MouseEvent) => {
                const failureTypeWithFailureCodes = store.failureTypes.find(
                    (ft) => ft.failure_type_id === row.failure_type.failure_type_id,
                ) || { ...row.failure_type, failure_codes: [] };
                dispatch.editEventReportData(row);
                dispatch.editEventReportData(row);
                setEventReportEditing(true);
                setEventReportEditData(row);
                handleFailureTypeChange(failureTypeWithFailureCodes);
                setCurrentFailureType(failureTypeWithFailureCodes);
                handleFailureCode(row.failure_code as IFailureCode);
                setCurrentFailureCode(row.failure_code as IFailureCode);
                if (row.area) {
                    const areaWithFunctionalLocations = store.machineCenter?.areas?.find(
                        (areaStore) => areaStore.area_id === row.area?.area_id,
                    ) || { ...row.area, functional_locations: [] };
                    handleArea(areaWithFunctionalLocations);
                    setCurrentArea(areaWithFunctionalLocations);
                }
                if (row.functional_location) {
                    handleFunctionalLocation(row.functional_location as IFunctionalLocation);
                    setCurrentFunctionalLocation(row.functional_location as IFunctionalLocation);
                }
                if (row.notes) {
                    handleNotes(row.notes);
                    setCurrentNotes(row.notes);
                }
                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS);

                if (process.env.REACT_APP_LOGS_ENABLED) {
                    const message = {
                        eventReportId: store.editEventReportData.row!.event_report_id,
                        editedByUserId: Number(operator),
                        currentFailureDetailsStepFromLocalStorage: currentFailureDetailsStepFromLocalStorage ?? null,
                        currentFailureTypeFromLocalStorage: failureType ?? null,
                        currentFailureCodeFromLocalStorage: failureCode ?? null,
                        currentFunctionalLocationFromLocalStorage: functionalLocation ?? null,
                        currentAreaFromLocalStorage: area ?? null,
                        currentNotesFromLocalStorage: notes ?? null,
                        isEventReportEditing: store.isEventReportEditing,
                    };

                    triggerMachineCenterLogs(
                        MachineCenterLogTriggersEnum.AT_EDIT_DOWNTIME_MENU_OPEN,
                        JSON.stringify(message),
                    );
                }
            };
        },
        [
            dispatch,
            handleSetFailureDetailStep,
            store,
            handleFailureTypeChange,
            handleFailureCode,
            handleArea,
            handleFunctionalLocation,
            handleNotes,
            area,
            functionalLocation,
            failureCode,
            failureType,
            notes,
            currentFailureDetailsStepFromLocalStorage,
            operator,
            triggerMachineCenterLogs,
        ],
    );

    const columns = useMemo(
        (): IDetailsColumns[] => [
            {
                id: "end_date",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.time_stop").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) => moment.utc(row.start_date).tz(userTimezone).format("MMM D, h:mm A"),
            },
            {
                id: "start_date",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.time_start").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) =>
                    row.end_date ? (
                        moment.utc(row.end_date).tz(userTimezone).format("h:mm A")
                    ) : (
                        <Typography variant="subtitle1" className={classes.waiting}>
                            {t("HMI.details.waiting")}
                        </Typography>
                    ),
            },
            {
                id: "duration",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.duration").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) =>
                    row.duration ? (
                        row.duration
                    ) : (
                        <Typography variant="subtitle1" className={classes.toBeDefined}>
                            {t("HMI.details.to_be_defined")}
                        </Typography>
                    ),
            },
            {
                id: "failure_type",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.type").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) => row.failure_type,
            },
            {
                id: "area",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.area").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) => row.area!,
            },
            {
                id: "functional_location",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.functional_location").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) => row.functional_location!,
            },
            {
                id: "failure_code",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.failure_code").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) => row.failure_code,
            },
            {
                id: "rca_triggered",
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("HMI.details.rca_triggered").toUpperCase()}
                    </Typography>
                ),
                hideSortIcon: false,
                render: (row) => "No",
            },
            {
                id: "edit",
                label: "",
                hideSortIcon: true,
                render: (row: IEventReport) => {
                    return (
                        <div style={{ position: "relative" }}>
                            {row.hmiEdit && (
                                <Alert severity="warning" icon={false} className={classes.alertMessage}>
                                    <CloudSyncIcon className={classes.icon} />
                                    <Typography
                                        style={{ fontFamily: "Open Sans", textAlign: "left", fontSize: "14px" }}
                                    >
                                        {t("HMI.details.update_pending")}
                                    </Typography>
                                </Alert>
                            )}
                            <button className={classes.button} onClick={handleEdit(row.fullEventReportObject!)}>
                                <ModeEditOutlineOutlinedIcon className={classes.editIcon} />
                            </button>
                        </div>
                    );
                },
            },
        ],
        [
            t,
            classes.columnHeader,
            classes.editIcon,
            handleEdit,
            classes.button,
            classes.waiting,
            classes.toBeDefined,
            userTimezone,
            classes.icon,
            classes.alertMessage,
        ],
    );

    return columns;
};
